<template>
  <div class="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col h-full"
       style="height: 520px;"
  >
    <div class="relative flex-shrink-0 h-[150px] overflow-hidden rounded-t-2xl bg-gray-200 bg-cover bg-center bg-no-repeat"
         :style="backgroundStyle"
    >
      <SaveToggle
          v-if="challenge.can_save"
          :challenge="challenge"
          @saved="emit('save', $event)"
          @unsaved="emit('unsave', $event)"
          class="absolute top-0 right-0"
      />
      <div class="absolute bottom-4 left-4">
        <div class="mb-2">
          <span class="px-3 py-1 bg-white text-zinc-800 rounded-full text-sm shadow">
            {{ challenge.type_label }}
          </span>
        </div>
        <div>
          <div class="px-3 py-1 bg-white text-zinc-800 flex rounded-full text-sm shadow">
            <img
                class="rounded-sm object-cover w-5 h-5 mr-1"
                :src="logoImage"
                :alt="__('Company logo image')"
            />
            <span>{{ challenge.company.name }}</span>
          </div>
        </div>
      </div>
      <div v-if="isNew" class="absolute top-11 right-0 flex items-center h-6 bg-primary rounded-l-full px-3">
        <Icon name="notifications_unread"  class="w-3.5 h-3.5 text-white" is-filled/>
        <span class="text-sm font-light text-white -ml-0.5">
          {{ __('New') }}
        </span>
      </div>
    </div>

    <div class="p-6 flex flex-col flex-1">
      <span class="text-lg font-medium text-zinc-800 line-clamp-2 min-h-[3.5rem] mb-4 pr-3">
        {{ challenge.title }}
      </span>

      <p class="text-gray-500 text-sm line-clamp-2 min-h-[2rem] mb-6">
        {{ challenge.description || __('No description provided') }}
      </p>

      <div v-if="challenge.has_completed && challenge.attempt && !challenge.attempt.needs_review" class="justify-between items-center flex mb-4 min-h-[3.5rem] px-2.5 py-3 bg-success/10 rounded-lg">
        <div class="text-sm text-gray-500">
          {{ __('Your score') }}
        </div>
        <div class="text-3xl font-medium text-zinc-800">
          {{ challenge.attempt.score || 0 }}
        </div>
      </div>
      <div v-else-if="challenge.has_completed && challenge.attempt && challenge.attempt.needs_review" class="flex items-center justify-between mb-6 min-h-[3.5rem]">
        <div>
          <div class="text-3xl font-medium text-zinc-800">
            {{ challenge.perfect_points }}
          </div>
          <div class="text-sm text-gray-500">
            {{ __('Points') }}
          </div>
        </div>
        <span class="px-2.5 py-1 text-base rounded-full font-medium bg-[#FBBF24] bg-opacity-10 text-[#FBBF24]">
    {{ __('In review') }}
  </span>
      </div>
      <div v-else class="flex items-center justify-between mb-6 min-h-[3.5rem]">
        <div>
          <div class="text-3xl font-medium text-zinc-800">
            {{ challenge.perfect_points }}
          </div>
          <div class="text-sm text-gray-500">
            {{ __('Points') }}
          </div>
        </div>
      </div>

      <div class="mb-6 line-clamp-2 min-h-[30px]">
        <ExpirationBar
            :start-date="challenge.start_at"
            :end-date="challenge.end_at"
            :never-expires="challenge.never_expires"
        />
      </div>

      <button
            v-if="!challenge.status"
          @click="startNow(challenge)"
          class="mt-auto w-full py-2 px-4 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/80  transition-colors"
      >
        {{ __('Start Challenge') }}
      </button>
      <button
          v-else-if="challenge.status === 'completed' || (challenge.status === 'saved' && challenge.is_completed)"
          @click="startNow(challenge)"
          class="mt-auto w-full py-2 px-4 text-primary rounded-lg border-2 border-primary text-sm font-medium hover:bg-primary/10  transition-colors"
      >
        {{ __('View Results') }}
      </button>
      <button
          v-else-if="challenge.status === 'in_review'"
           @click="startNow(challenge)"
          class="mt-auto w-full py-2 px-4 text-zinc-400 border-2 border-zinc-400 rounded-lg text-sm font-medium text-center">
        {{ __('Pending Results') }}
      </button>
      <button
          v-else-if="challenge.status === 'saved' || challenge.status === 'in_progress'"
          @click="resumeChallenge(challenge)"
          class="mt-auto w-full py-2 px-4 text-primary border-2 border-primary rounded-lg text-sm font-medium hover:bg-primary/10 transition-colors"
      >
        {{ __('Continue Challenge') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import ExpirationBar from "@/pages/company/challenges/components/ExpirationBar.vue";
import {Icon} from "@/components/block/icon";
import {__} from "@/helpers";
import SaveToggle from "@/pages/candidate/challenges/components/SaveToggle.vue";

const props = defineProps({
  challenge: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['delete', 'toggle-status', 'edit', 'save', 'unsave']);

const redirectToPage = (name, params) => {
  window.location.href = route(name, params);
};

const logoImage = computed(() => {
  const logo = props.challenge.company.logo;
  return logo
      ? `/uploads/${logo}`
      : '/images/logo_placeholder.png';
});

const startNow = (challenge) => {
  redirectToPage('redesign.candidate.challenges.show', { challenge: challenge.id});
};

const resumeChallenge = (challenge) => {
  redirectToPage('redesign.candidate.challenges.show', { challenge: challenge.id });
};

const isNew = computed(() => {
  const start = new Date(props.challenge.start_at);
  const now = new Date();
  const diffMs = now - start;
  return diffMs >= 0 && diffMs <= 24 * 60 * 60 * 1000;
});

const svgPositionMap = {
  'object-1.svg': { size: '65%', position: '60% 100%' },
  'object-2.svg': { size: '95%', position: '-200% -100%' },
  'object-3.svg': { size: '65%', position: '65% 20%' },
  'object-4.svg': { size: '80%', position: '60% 0%' },
  'object-5.svg': { size: '65%', position: '0% 15%' },
  'object-6.svg': { size: '65%', position: '25% 0%' },
  'object-7.svg': { size: '45%', position: '100% 50%' },
  'object-8.svg': { size: '65%', position: '115% 20%' },

  'default':  { size: '65%', position: '50% 0%' }
};

const backgroundStyle = computed(() => {
  const url = props.challenge.preview_image_url;
  const isSvg = props.challenge.is_random_preview;

  if (isSvg) {
    const svgName = props.challenge.preview_image
    const svgProperties = svgPositionMap[svgName] || svgPositionMap.default;

    return {
      backgroundImage: `url('${url}')`,
      backgroundSize: svgProperties.size,
      backgroundPosition: svgProperties.position,
      backgroundColor: 'black',
    }
  }
  return {
    backgroundImage: `url('${props.challenge.preview_image_url}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  }

});
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
